'use client';
import { useState, useEffect } from 'react';
 import Icon from'@/components/ui/AppIcon';

interface FAQItem {
  id: string
  question: string
  answer: string
}

const faqs: FAQItem[] = [
  {
    id: 'faq_payment',
    question: 'How does pricing work?',
    answer: 'You pay an unlock fee (starting at $3) plus a per-minute rate. Payment is processed automatically when you end your ride. No hidden fees.',
  },
  {
    id: 'faq_cancel',
    question: 'Can I reserve a scooter?',
    answer: 'Yes, you can reserve a scooter for up to 15 minutes. After that, it becomes available to other riders. No charge for reservations.',
  },
  {
    id: 'faq_schedule',
    question: 'Where can I park the scooter?',
    answer: 'Park only in designated parking zones shown in the app. Improper parking may result in additional fees. Always ensure the scooter is upright and not blocking walkways.',
  },
  {
    id: 'faq_luggage',
    question: 'Do I need a helmet?',
    answer: 'Helmets are provided in the scooter storage compartment and are required by law in many cities. Always wear a helmet for your safety.',
  },
  {
    id: 'faq_pets',
    question: 'What if the scooter has issues?',
    answer: 'If you encounter any problems, use the in-app "Report Issue" button. You won\'t be charged for rides on malfunctioning scooters. Our team responds within minutes.',
  },
]

export default function BookingFAQ() {
  const [mounted, setMounted] = useState(false)
  const [openFAQ, setOpenFAQ] = useState<string | null>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={`faq_skeleton_${i}`} className="card p-6 animate-pulse">
            <div className="h-6 bg-muted rounded w-3/4" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-primary mb-3">
            Need Help?
          </h2>
          <p className="text-muted-foreground font-body">
            Find answers to common scooter rental questions
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* FAQ Accordion */}
          <div className="md:col-span-2 space-y-4">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="card overflow-hidden transition-all"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === faq.id ? null : faq.id)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-muted/50 transition-colors"
                >
                  <span className="font-display font-bold text-primary pr-4">
                    {faq.question}
                  </span>
                  <Icon
                    name="ChevronDownIcon"
                    size={20}
                    className={`text-muted-foreground flex-shrink-0 transition-transform ${
                      openFAQ === faq.id ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFAQ === faq.id && (
                  <div className="px-6 pb-6">
                    <p className="text-foreground/70 font-body leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact Card */}
          <div className="md:col-span-1">
            <div className="card p-6 bg-gradient-to-br from-secondary/5 to-accent/5 sticky top-24">
              <div className="text-center space-y-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary/10">
                  <Icon name="ChatBubbleLeftRightIcon" size={32} className="text-secondary" />
                </div>
                <h3 className="font-display font-bold text-primary">
                  Still Have Questions?
                </h3>
                <p className="text-sm text-muted-foreground font-body">
                  Our support team is available 24/7 to help you
                </p>
                <div className="space-y-3 pt-4">
                  <button className="w-full btn-secondary py-3 text-sm">
                    <Icon name="ChatBubbleLeftIcon" size={18} className="inline mr-2" />
                    Live Chat
                  </button>
                  <a
                    href="tel:1-800-SCOOTRIDE"
                    className="w-full btn-primary py-3 text-sm inline-flex items-center justify-center"
                  >
                    <Icon name="PhoneIcon" size={18} className="mr-2" />
                    1-800-SCOOTRIDE
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}